<template>
  <div class="card card-custom">
    <div class="card-body container-fluid py-8 px-8 py-lg-10 px-lg-10">
      <WorkQueueChrome
        :active-lane="'clients'"
        :crumbs="crumbs"
        subtitle="Client page"
        @lane="goLane"
        @search="onChromeSearch"
      />

      <b-alert v-if="errorAlert" variant="warning" show>
        <div class="text-dark">{{ errorAlert }}</div>
      </b-alert>

      <div v-if="loaded && card">
        <div class="mb-6">
          <h2 class="font-weight-bolder text-dark mb-1">{{ card.client.name }}</h2>
          <div class="text-muted">
            <span v-if="card.client.tradingName">Trading as {{ card.client.tradingName }}</span>
            <span v-if="card.client.tradingName && (card.client.state || card.client.brokerName)"> · </span>
            <span v-if="card.client.state">{{ card.client.state }}</span>
            <span v-if="card.client.state && card.client.brokerName"> · </span>
            <span v-if="card.client.brokerName">{{ card.client.brokerName }}</span>
          </div>
        </div>

        <ul class="nav nav-tabs nav-tabs-line mb-6">
          <li v-for="tab in tabs" :key="tab.key" class="nav-item">
            <a
              href="#"
              class="nav-link"
              :class="{ active: activeTab === tab.key }"
              @click.prevent="activeTab = tab.key"
            >{{ tab.label }}</a>
          </li>
        </ul>

        <!-- Overview -->
        <div v-if="activeTab === 'overview'">
          <div class="border rounded p-5 mb-5 bg-light-warning" v-if="card.actionNeeded">
            <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">Action needed</div>
            <div class="font-size-lg text-dark mb-4">{{ card.actionNeeded.message }}</div>

            <div v-if="billingStepKey" class="mb-4">
              <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">Billing steps</div>
              <div class="d-flex flex-wrap">
                <span
                  v-for="step in billingChecklist"
                  :key="step.key"
                  class="badge badge-pill mr-2 mb-2 px-3 py-2"
                  :class="step.current ? 'badge-primary' : (step.done ? 'badge-success' : 'badge-light')"
                >
                  {{ step.n }}. {{ step.label }}
                </span>
              </div>
            </div>

            <div
              v-if="producingBrokerConnection"
              class="border rounded bg-white p-4 mb-4"
            >
              <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">
                Connected producing broker
                <span
                  class="badge badge-pill ml-2"
                  :class="producingBrokerConnection.connected ? 'badge-success' : 'badge-warning'"
                >
                  {{ producingBrokerConnection.connected ? 'Connected' : 'Missing leg' }}
                </span>
              </div>
              <div class="font-size-sm text-dark mb-1" v-if="producingBrokerConnection.zoho">
                Zoho:
                <a
                  v-if="producingBrokerConnection.zoho.url"
                  :href="producingBrokerConnection.zoho.url"
                  target="_blank"
                  rel="noopener"
                >{{ producingBrokerConnection.zoho.name || producingBrokerConnection.zoho.id }}</a>
                <span v-else>{{ producingBrokerConnection.zoho.name || producingBrokerConnection.zoho.id }}</span>
              </div>
              <div class="font-size-sm text-dark mb-1">
                INSIGHT:
                <template v-if="producingBrokerConnection.insight">
                  <a
                    v-if="producingBrokerConnection.insight.url"
                    :href="producingBrokerConnection.insight.url"
                    target="_blank"
                    rel="noopener"
                  >{{ producingBrokerConnection.insight.id }}</a>
                  <span v-else>{{ producingBrokerConnection.insight.id }}</span>
                </template>
                <span v-else class="text-warning">missing</span>
              </div>
              <div class="font-size-sm text-dark mb-2">
                Neon:
                <span v-if="producingBrokerConnection.neon">{{ producingBrokerConnection.neon.partyRef }}</span>
                <span v-else class="text-warning">missing</span>
              </div>
              <div
                v-if="producingBrokerConnection.primaryCta && producingBrokerConnection.primaryCta.detail"
                class="text-muted font-size-sm mb-2"
              >
                {{ producingBrokerConnection.primaryCta.detail }}
              </div>
              <button
                v-if="brokerEnsureGoldVisible"
                class="btn btn-light-warning font-weight-bold text-uppercase mr-2 mb-2"
                :disabled="busy"
                @click.prevent="doEnsureBrokerGold"
              >
                Ensure Gold party
              </button>
            </div>

            <div
              v-if="clientConnection && (billingStepKey === 'link-for-billing' || clientConnection.missingLegs.length)"
              class="border rounded bg-white p-4 mb-4"
            >
              <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">
                Connected client
                <span
                  class="badge badge-pill ml-2"
                  :class="clientConnection.connected ? 'badge-success' : 'badge-warning'"
                >
                  {{ clientConnection.connected ? 'Connected' : 'Missing leg' }}
                </span>
              </div>
              <div class="font-size-sm text-dark mb-1" v-if="clientConnection.zoho">
                Zoho:
                <a
                  v-if="clientConnection.zoho.url"
                  :href="clientConnection.zoho.url"
                  target="_blank"
                  rel="noopener"
                >{{ clientConnection.zoho.name || clientConnection.zoho.id }}</a>
                <span v-else>{{ clientConnection.zoho.name || clientConnection.zoho.id }}</span>
              </div>
              <div class="font-size-sm text-dark mb-1">
                INSIGHT:
                <span v-if="clientConnection.insight">{{ clientConnection.insight.id }}</span>
                <span v-else class="text-warning">missing</span>
              </div>
              <div class="font-size-sm text-dark mb-2">
                Neon:
                <span v-if="clientConnection.neon">{{ clientConnection.neon.partyRef }}</span>
                <span v-else class="text-warning">missing</span>
              </div>
              <div
                v-if="clientConnection.primaryCta && clientConnection.primaryCta.detail"
                class="text-muted font-size-sm mb-2"
              >
                {{ clientConnection.primaryCta.detail }}
              </div>
              <button
                v-if="clientEnsureGoldVisible"
                class="btn btn-light-warning font-weight-bold text-uppercase mr-2 mb-2"
                :disabled="busy"
                @click.prevent="doEnsureClientGold"
              >
                Ensure Gold party
              </button>
            </div>

            <div v-if="showBrokerPanel" class="border rounded bg-white p-4 mb-4">
              <div class="text-muted font-size-sm mb-2" v-if="brokerStatus && brokerStatus.subagentName">
                Zoho broker: <strong class="text-dark">{{ brokerStatus.subagentName }}</strong>
                <span v-if="brokerStatus.autoMatch"> · match: {{ brokerStatus.autoMatch.name }}</span>
              </div>
              <div class="d-flex mb-3" v-if="needsBrokerPick">
                <input
                  v-model="brokerSearch"
                  type="search"
                  class="form-control mr-2"
                  placeholder="Search INSIGHT producing broker"
                  @keyup.enter="runBrokerSearch"
                />
                <button class="btn btn-light-primary font-weight-bold" :disabled="busy" @click.prevent="runBrokerSearch">
                  Find
                </button>
              </div>
              <button
                v-for="c in brokerCandidates"
                :key="c.id || c.name"
                type="button"
                class="btn btn-block text-left mb-2"
                :class="pickedBrokerId && String(pickedBrokerId) === String(c.id) ? 'btn-light-primary' : 'btn-light'"
                :disabled="!c.id || busy"
                @click.prevent="pickedBrokerId = c.id"
              >
                {{ c.name }}
              </button>
            </div>

            <div class="d-flex flex-wrap">
              <button
                v-if="card.actionNeeded.primary"
                class="btn btn-primary font-weight-bold text-uppercase mr-3 mb-2"
                :disabled="busy || brokerPrimaryDisabled"
                @click.prevent="runAction(card.actionNeeded.primary)"
              >
                {{ card.actionNeeded.primary.label }}
              </button>
              <button
                v-if="card.actionNeeded.secondary"
                class="btn btn-light-primary font-weight-bold text-uppercase mb-2"
                :disabled="busy"
                @click.prevent="runAction(card.actionNeeded.secondary)"
              >
                {{ card.actionNeeded.secondary.label }}
              </button>
            </div>
          </div>

          <div v-if="card.currentCover" class="border rounded p-5 mb-5">
            <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">Current cover</div>
            <div class="font-weight-bolder text-dark">
              {{ card.currentCover.productLabel || card.currentCover.brand }} · {{ card.currentCover.policyNumber }}
            </div>
            <div class="text-muted">
              {{ formatDate(card.currentCover.startDate) }} to {{ formatDate(card.currentCover.endDate) }}
            </div>
            <div class="mt-2 text-dark">
              <span v-if="card.currentCover.vehicleCount != null">{{ card.currentCover.vehicleCount }} vehicles · </span>
              <span v-if="card.currentCover.premium != null">
                Premium {{ card.currentCover.premium | currency("$", ",", 0, ".", "front", true) }}
              </span>
              <div v-if="card.currentCover.premiumHonesty" class="text-warning font-size-sm mt-1">
                {{ card.currentCover.premiumHonesty }}
              </div>
            </div>
            <button class="btn btn-sm btn-light-primary font-weight-bold text-uppercase mt-4" @click.prevent="openPolicy(card.currentCover.id)">
              Open policy
            </button>
          </div>

          <div class="row">
            <div class="col-md-6 mb-5">
              <div class="border rounded p-5 h-100">
                <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">Claims</div>
                <div v-if="card.claims.lossRatio != null">
                  <div class="font-size-h4 font-weight-bolder text-dark">Loss ratio: {{ card.claims.lossRatio }}%</div>
                  <div class="text-muted">{{ card.claims.lossRatioExplanation }}</div>
                  <div v-if="card.claims.periodLabel" class="text-muted font-size-sm">{{ card.claims.periodLabel }}</div>
                </div>
                <div v-else class="text-muted">{{ card.claims.unavailableReason }}</div>
                <div class="mt-3 text-dark">
                  {{ card.claims.openCount }} open claims
                  <span v-if="card.claims.largeLossCount"> · {{ card.claims.largeLossCount }} large loss</span>
                </div>
                <button class="btn btn-sm btn-link px-0 mt-2" @click.prevent="activeTab = 'claims'">View claims</button>
              </div>
            </div>
            <div class="col-md-6 mb-5">
              <div class="border rounded p-5 h-100">
                <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-2">Fleet</div>
                <div v-if="card.fleet.available">
                  <div class="font-size-h4 font-weight-bolder text-dark">{{ card.fleet.vehicleCount }} vehicles</div>
                  <div class="text-muted" v-if="card.fleet.mainTypes && card.fleet.mainTypes.length">
                    Main types: {{ card.fleet.mainTypes.join(", ") }}
                  </div>
                  <div class="text-muted" v-if="card.fleet.mainState">Mostly {{ card.fleet.mainState }}</div>
                </div>
                <div v-else class="text-muted">{{ card.fleet.message }}</div>
                <button class="btn btn-sm btn-link px-0 mt-2" @click.prevent="activeTab = 'fleet'">View fleet</button>
              </div>
            </div>
          </div>

          <div v-if="card.otherWork && card.otherWork.length" class="border rounded p-5 mb-5">
            <div class="text-uppercase font-weight-bold text-muted font-size-sm mb-3">Other work</div>
            <div v-for="work in card.otherWork" :key="work.id" class="d-flex justify-content-between align-items-center py-2 border-bottom">
              <div>{{ work.message }}</div>
              <button class="btn btn-sm btn-light-primary font-weight-bold" @click.prevent="go(work.route)">
                {{ work.label || "Review" }}
              </button>
            </div>
          </div>
        </div>

        <!-- Policies and quotes -->
        <div v-if="activeTab === 'policies'">
          <h4 class="font-weight-bold mb-4">Policies</h4>
          <div v-if="!(card.policies || []).length" class="text-muted mb-6">No policies found.</div>
          <div
            v-for="policy in card.policies"
            :key="policy.id"
            class="d-flex justify-content-between align-items-center border rounded p-4 mb-3"
          >
            <div>
              <div class="font-weight-bold text-dark">{{ policy.policyNumber }}</div>
              <div class="text-muted font-size-sm">
                {{ policy.status }} · {{ formatDate(policy.startDate) }} to {{ formatDate(policy.endDate) }}
                <span v-if="policy.premium != null">
                  · {{ policy.premium | currency("$", ",", 0, ".", "front", true) }}
                </span>
              </div>
              <div v-if="policy.premiumHonesty" class="text-warning font-size-sm">{{ policy.premiumHonesty }}</div>
            </div>
            <button class="btn btn-sm btn-light-primary font-weight-bold text-uppercase" @click.prevent="openPolicy(policy.id)">
              Open policy
            </button>
          </div>

          <h4 class="font-weight-bold mb-4 mt-8">Quotes</h4>
          <div v-if="!(card.quotes || []).length" class="text-muted">No quotes found.</div>
          <div
            v-for="quote in card.quotes"
            :key="quote.id"
            class="d-flex justify-content-between align-items-center border rounded p-4 mb-3"
          >
            <div>
              <div class="font-weight-bold text-dark">{{ quote.name || "Quote" }}</div>
              <div class="text-muted font-size-sm">
                {{ quote.status }}
                <span v-if="quote.premium != null">
                  · {{ quote.premium | currency("$", ",", 0, ".", "front", true) }}
                </span>
              </div>
            </div>
            <button
              class="btn btn-sm btn-primary font-weight-bold text-uppercase"
              :disabled="busy"
              @click.prevent="copyQuote(quote)"
            >
              Copy quote
            </button>
          </div>
        </div>

        <!-- Claims -->
        <div v-if="activeTab === 'claims'">
          <div v-if="card.claims.lossRatio != null" class="mb-5">
            <div class="font-size-h4 font-weight-bolder">Loss ratio: {{ card.claims.lossRatio }}%</div>
            <div class="text-muted">{{ card.claims.lossRatioExplanation }}</div>
          </div>
          <div v-else class="text-muted mb-5">{{ card.claims.unavailableReason }}</div>
          <div v-if="!(card.claims.rows || []).length" class="text-muted">No claim rows to show.</div>
          <div v-for="row in card.claims.rows" :key="row.id" class="border rounded p-3 mb-2">
            <div class="font-weight-bold">{{ formatDate(row.date) }} · {{ row.type || "Claim" }}</div>
            <div class="text-muted font-size-sm">
              {{ row.status }}
              <span v-if="row.incurred != null">
                · {{ row.incurred | currency("$", ",", 0, ".", "front", true) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Fleet -->
        <div v-if="activeTab === 'fleet'">
          <div v-if="!card.fleet.available" class="text-muted">{{ card.fleet.message }}</div>
          <div v-else>
            <div class="mb-4 text-dark">
              {{ card.fleet.vehicleCount }} vehicles
              <span v-if="card.fleet.sumInsured != null">
                · sum insured {{ card.fleet.sumInsured | currency("$", ",", 0, ".", "front", true) }}
              </span>
            </div>
            <div v-for="vehicle in card.fleet.vehicles" :key="vehicle.id" class="border rounded p-3 mb-2">
              <div class="font-weight-bold">{{ vehicle.registration || "Vehicle" }}</div>
              <div class="text-muted font-size-sm">
                {{ [vehicle.make, vehicle.model, vehicle.type, vehicle.state].filter(Boolean).join(" · ") }}
              </div>
            </div>
          </div>
        </div>

        <!-- Record details -->
        <div class="mt-8 border-top pt-4">
          <a href="#" class="text-muted font-size-sm" @click.prevent="showDetails = !showDetails">
            {{ showDetails ? "Hide record details" : "Record details" }}
          </a>
          <div v-if="showDetails" class="mt-3 font-size-sm text-muted">
            <div v-if="card.recordDetails.crmClientId">CRM client: {{ card.recordDetails.crmClientId }}</div>
            <div v-if="card.recordDetails.insightClientId">Billing system client: {{ card.recordDetails.insightClientId }}</div>
            <div v-if="card.recordDetails.partyRef">Master record ref: {{ card.recordDetails.partyRef }}</div>
            <div v-if="card.recordDetails.crmUrl" class="mt-2">
              <a :href="card.recordDetails.crmUrl" target="_blank" rel="noopener">Open in CRM</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME } from "@/core/services/store/htmlclass.module";
import WorkQueueChrome from "./components/WorkQueueChrome.vue";
import {
  getClientCard,
  fixClientLink,
  copyPriorQuote,
  getBrokerLinkStatus,
  linkClientBroker,
  ensureBrokerGold,
  ensureClientGold,
  searchInsightProducingBrokers
} from "@/services/work-queue-service";

const BILLING_STEPS = ["link-broker", "link-for-billing", "create-billing-work", "prepare-billing"];

export default {
  name: "ClientPage",
  components: { WorkQueueChrome },
  data() {
    return {
      loaded: false,
      busy: false,
      errorAlert: null,
      card: null,
      activeTab: "overview",
      showDetails: false,
      brokerStatus: null,
      brokerSearch: "",
      brokerCandidates: [],
      pickedBrokerId: null,
      tabs: [
        { key: "overview", label: "Overview" },
        { key: "policies", label: "Policies and quotes" },
        { key: "claims", label: "Claims" },
        { key: "fleet", label: "Fleet" }
      ]
    };
  },
  computed: {
    crumbs() {
      const name = this.card && this.card.client ? this.card.client.name : "Client";
      return [{ label: name }];
    },
    billingStepKey() {
      const primary = this.card && this.card.actionNeeded && this.card.actionNeeded.primary;
      const code = this.card && this.card.actionNeeded && this.card.actionNeeded.code;
      if (!primary && !code) return null;
      if ((primary && primary.key === "link-broker") || code === "link_broker") return "link-broker";
      if (
        (primary && (primary.key === "fix-client-link" || primary.key === "ensure-client-gold"))
        || code === "fix_client_link"
      ) {
        return "link-for-billing";
      }
      if (
        (primary && primary.key === "create-billing-work")
        || code === "create_billing_work"
        || (primary && primary.route && String(primary.route).indexOf("create") >= 0)
      ) {
        return "create-billing-work";
      }
      if (primary && primary.route && String(primary.route).indexOf("billing-processing") >= 0) {
        return "prepare-billing";
      }
      return null;
    },
    billingChecklist() {
      const cur = this.billingStepKey;
      if (!cur) return [];
      const curIdx = BILLING_STEPS.indexOf(cur);
      const labels = {
        "link-broker": "Link broker",
        "link-for-billing": "Link for billing",
        "create-billing-work": "Create billing work",
        "prepare-billing": "Prepare"
      };
      return BILLING_STEPS.map((key, i) => ({
        key,
        n: i + 1,
        label: labels[key],
        done: curIdx >= 0 ? i < curIdx : false,
        current: key === cur
      }));
    },
    showBrokerPanel() {
      return this.billingStepKey === "link-broker";
    },
    needsBrokerPick() {
      if (!this.showBrokerPanel) return false;
      if (this.brokerStatus && this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) return false;
      return true;
    },
    brokerPrimaryDisabled() {
      if (!this.showBrokerPanel) return false;
      if (this.brokerStatus && this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) return false;
      if (!this.needsBrokerPick) return false;
      return !this.pickedBrokerId;
    },
    workContext() {
      const q = this.$route.query || {};
      const cover = this.card && this.card.currentCover;
      const details = this.card && this.card.recordDetails;
      return {
        transactionId: q.transactionId || undefined,
        policyId: q.policyId || (cover && cover.id) || undefined,
        rowSubagentId: q.rowSubagentId || (details && details.subagentId) || undefined
      };
    },
    producingBrokerConnection() {
      if (this.brokerStatus && this.brokerStatus.producingBrokerConnection) {
        return this.brokerStatus.producingBrokerConnection;
      }
      return (this.card && this.card.producingBrokerConnection) || null;
    },
    clientConnection() {
      return (this.card && this.card.clientConnection) || null;
    },
    brokerEnsureGoldVisible() {
      const c = this.producingBrokerConnection;
      if (!c || !c.zoho) return false;
      return !c.neon || (c.primaryCta && c.primaryCta.key === "ensure-broker-gold");
    },
    clientEnsureGoldVisible() {
      const c = this.clientConnection;
      if (!c) return false;
      return !c.neon || (c.primaryCta && c.primaryCta.key === "ensure-client-gold");
    }
  },
  watch: {
    "$route.params.clientId": function() {
      this.loadCard();
    }
  },
  mounted() {
    this.$store.commit("setWizardTitle", "Underwriting Work Queue");
    this.$store.commit("setWizardSubtitle", "Client");
    if (this.$route.query.tab === "policies") {
      this.activeTab = "policies";
    }
    this.loadCard();
  },
  methods: {
    formatDate(isoDate) {
      if (!isoDate) return "";
      return moment.tz(String(isoDate).slice(0, 10), "YYYY-MM-DD", "Australia/Sydney").format("DD/MM/YYYY");
    },
    goLane(lane) {
      this.$router.push({ path: "/workbench", query: { lane } }).catch(() => {});
    },
    onChromeSearch(q) {
      this.$router.push({ path: "/workbench", query: { lane: "clients", q } }).catch(() => {});
    },
    go(route) {
      if (route) this.$router.push(route).catch(() => {});
    },
    openPolicy(policyId) {
      if (policyId) this.$router.push(`/workbench/policies/${policyId}`).catch(() => {});
    },
    apiError(e, fallback) {
      const data = e && e.response && e.response.data;
      const detail = data && (data.error || data.message || data.result);
      return detail ? String(detail) : fallback;
    },
    resetBrokerUi() {
      this.brokerStatus = null;
      this.brokerSearch = "";
      this.brokerCandidates = [];
      this.pickedBrokerId = null;
    },
    async loadBrokerStatus() {
      try {
        const status = await getBrokerLinkStatus(this.$route.params.clientId, this.workContext);
        this.brokerStatus = status || null;
        if (status && Array.isArray(status.candidates)) {
          this.brokerCandidates = status.candidates.filter(c => c && c.id);
          if (this.brokerCandidates.length === 1) {
            this.pickedBrokerId = this.brokerCandidates[0].id;
          }
        }
        if (status && status.subagentName) {
          this.brokerSearch = status.subagentName;
        }
      } catch (e) {
        this.brokerStatus = null;
        this.errorAlert = this.apiError(e, "Unable to load broker link status.");
      }
    },
    async runBrokerSearch() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await searchInsightProducingBrokers(this.brokerSearch);
        if (result && result.error) {
          this.errorAlert = result.error;
          this.brokerCandidates = [];
          return;
        }
        const rows = Array.isArray(result && result.results) ? result.results : [];
        this.brokerCandidates = rows.map(r => ({
          id: r.id || r.insightBrokerId,
          name: r.name
        })).filter(r => r.id && r.name);
        if (this.brokerCandidates.length === 1) {
          this.pickedBrokerId = this.brokerCandidates[0].id;
        }
      } catch (e) {
        this.errorAlert = this.apiError(e, "Unable to search producing brokers.");
      } finally {
        this.busy = false;
      }
    },
    async loadCard() {
      this.loaded = false;
      this.errorAlert = null;
      this.resetBrokerUi();
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      try {
        const result = await getClientCard(this.$route.params.clientId);
        if (result && result.error) {
          this.card = null;
          this.errorAlert = result.error;
          this.loaded = false;
          return;
        }
        this.card = result;
        this.loaded = true;
        const primary = result && result.actionNeeded && result.actionNeeded.primary;
        const needsBrokerStatus = (primary && primary.key === "link-broker")
          || (result && result.producingBrokerConnection && !result.producingBrokerConnection.connected);
        if (needsBrokerStatus) {
          await this.loadBrokerStatus();
        }
      } catch (e) {
        const detail =
          (e && e.response && e.response.data && (e.response.data.error || e.response.data.message || e.response.data.result))
          || (e && e.message)
          || null;
        this.card = null;
        this.errorAlert = detail
          ? String(detail)
          : "Unable to load this client right now. Go back to search and try again.";
      } finally {
        this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
      }
    },
    async runAction(action) {
      if (!action) return;
      if (action.route) {
        this.go(action.route);
        return;
      }
      if (action.key === "link-broker") {
        await this.doLinkBroker();
        return;
      }
      if (action.key === "ensure-client-gold") {
        await this.doEnsureClientGold();
        return;
      }
      if (action.key === "fix-client-link") {
        await this.doFixLink();
        return;
      }
      if (action.key === "copy-quote" && action.quoteId) {
        await this.copyQuote({ id: action.quoteId }, action.target || "renewal", action.policyId);
      }
    },
    async doLinkBroker() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const body = { ...this.workContext };
        if (this.pickedBrokerId) {
          body.insightBrokerId = this.pickedBrokerId;
        } else if (this.brokerStatus && this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) {
          body.insightBrokerId = this.brokerStatus.autoMatch.id;
        }
        const result = await linkClientBroker(this.$route.params.clientId, body);
        if (result && result.needsPick) {
          this.brokerCandidates = (result.candidates || []).filter(c => c && c.id);
          this.errorAlert = result.error || "Pick the matching INSIGHT producing broker, then link.";
          return;
        }
        if (result && result.error) {
          this.errorAlert = result.error;
          if (Array.isArray(result.candidates)) {
            this.brokerCandidates = result.candidates.filter(c => c && c.id);
          }
          return;
        }
        await this.loadCard();
      } catch (e) {
        this.errorAlert = this.apiError(e, "Unable to link the producing broker right now.");
      } finally {
        this.busy = false;
      }
    },
    async doEnsureBrokerGold() {
      if (!window.confirm("Create or adopt the Neon Gold party for this producing broker and stamp it on Zoho?")) {
        return;
      }
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await ensureBrokerGold(this.$route.params.clientId, {
          ...this.workContext,
          confirmed: true
        });
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        await this.loadCard();
      } catch (e) {
        this.errorAlert = this.apiError(e, "Unable to ensure the producing broker Gold party right now.");
      } finally {
        this.busy = false;
      }
    },
    async doEnsureClientGold() {
      if (!window.confirm("Adopt an existing Neon Gold party onto this Zoho client? Workbench will not mint a new Gold party.")) {
        return;
      }
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await ensureClientGold(this.$route.params.clientId, {
          ...this.workContext,
          confirmed: true
        });
        // Soft-fail stays in the success envelope — never treat missing/empty as success.
        if (!result) {
          this.errorAlert = "Ensure Gold party returned no result. Try again, or check the server response.";
          return;
        }
        if (result.error) {
          this.errorAlert = result.error;
          return;
        }
        if (!result.done && !result.partyRef) {
          this.errorAlert = result.detail
            || "Ensure Gold party did not adopt a Neon party. No change was made.";
          return;
        }
        const successDetail = result.detail || (result.partyRef
          ? `Adopted Gold party ${result.partyRef}.`
          : null);
        await this.loadCard();
        const neon = this.clientConnection && this.clientConnection.neon
          && this.clientConnection.neon.partyRef;
        if (!neon) {
          this.errorAlert = successDetail
            ? `${successDetail} Zoho still shows no Party_Ref after reload — retry Ensure Gold party.`
            : "Ensure Gold party finished but Zoho still shows no Party_Ref. Retry Ensure Gold party.";
          return;
        }
        if (successDetail) {
          this.errorAlert = successDetail;
        }
      } catch (e) {
        this.errorAlert = this.apiError(e, "Unable to ensure the client Gold party right now.");
      } finally {
        this.busy = false;
      }
    },
    async doFixLink() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const body = { ...this.workContext };
        if (this.card && this.card.currentCover && this.card.currentCover.brand) {
          body.brand = this.card.currentCover.brand;
        }
        const result = await fixClientLink(this.$route.params.clientId, body);
        if (!result) {
          this.errorAlert = "Link for billing returned no result. Try again.";
          return;
        }
        if (result.needsGold) {
          this.errorAlert = result.error
            || "This client needs a Gold party first. Use Ensure Gold party, then Link for billing.";
          return;
        }
        if (result.error) {
          this.errorAlert = result.error;
          return;
        }
        await this.loadCard();
      } catch (e) {
        // sendErrorResponse puts the message in `result`; gate 403s use `error`.
        this.errorAlert = this.apiError(e, "Unable to fix the client link right now.");
      } finally {
        this.busy = false;
      }
    },
    async copyQuote(quote, target = "renewal", policyId = null) {
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await copyPriorQuote(quote.id, {
          target,
          policyId: policyId || quote.policyId || null
        });
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        if (result && result.wizardUrl) {
          // Prefer in-app route when possible
          const match = String(result.wizardUrl).match(/nb-rn\?(.+)$/);
          if (match) {
            this.$router.push(`/wizard/nb-rn?${match[1]}&from=client:${this.$route.params.clientId}`).catch(() => {});
          } else if (result.transactionId) {
            this.$router
              .push(`/wizard/nb-rn?transactionId=${result.transactionId}&from=client:${this.$route.params.clientId}`)
              .catch(() => {});
          }
        }
      } catch (_e) {
        this.errorAlert = "Unable to copy the quote right now.";
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
